import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert, Box, Button, Chip, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import BaseConfigModal, { MODAL_SECTION_LABEL_SX } from "@plannel/components/modal/BaseConfigModal";
import { FS } from "@plannel/utils/font-util";
import { C } from "@plannel/components/ai/aiTokens";
import TermAliasRow from "@plannel/pages/ai/term/TermAliasRow";
import { addedSx } from "@plannel/pages/ai/term/TermAliasCell";
import { toneDark, toneMain } from "@plannel/pages/ai/term/termColors";
import {
  DEF_MAX_LEN, LOCALES, LOCALE_LABELS, NAME_MAX_LEN, NOTE_MAX_LEN, STATUS,
  isEmptyDraft, rowStatus, sortLocales, termName, toDraft, uiLocale,
} from "@plannel/pages/ai/term/termUtil";

// 본문 높이 고정 — 모드·내용에 따라 모달 외형이 바뀌지 않게 하고 안쪽만 스크롤
const BODY_H = "min(560px, 64vh)";
const STATUS_BADGE = {
  [STATUS.OVERRIDE]: { key: "aiTermStatusOverride", tone: "override" },
  [STATUS.ADDED]: { key: "aiTermStatusAdded", tone: "added" },
};

// 정의·보충 설명 입력은 본문 크기(FS.BODY) — MUI 기본(16px)은 모달의 다른 글자보다 크다
const TEXT_FIELD_SX = { "& .MuiInputBase-input": { fontSize: FS.BODY, lineHeight: 1.5 } };

const SectionHead = ({ label, right }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, minHeight: 20 }}>
    <Typography sx={MODAL_SECTION_LABEL_SX}>{label}</Typography>
    {right}
  </Box>
);

const Caption = ({ children, sx }) => (
  <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", ...sx }}>{children}</Typography>
);

// 범례 — 별칭 칩과 같은 모양·색의 작은 칩(표준=회색 채움, 추가=제품색 테두리, 제외=주황 테두리·취소선)
const LegendChip = ({ label, sx }) => (
  <Box component="span" sx={{
    display: "inline-block", height: 18, lineHeight: "18px", px: 0.75, borderRadius: "4px", fontSize: FS.CAPTION, ...sx,
  }}>
    {label}
  </Box>
);

// 용어 1건 편집 — 기존 행(row)이면 차분 수정, row 가 없으면 테넌트 추가어 신규 등록
const TermEditDialog = ({ open, row, locale, onClose, onSave, onRevert, saving = false }) => {
  const { t, i18n } = useTranslation();
  // 언어 줄은 UI 언어를 맨 앞에(편집할 언어가 먼저 보이게). UI 언어를 바꾸면 곧바로 다시 정렬된다
  const langs = sortLocales(uiLocale(i18n.language));
  const isNew = !row;
  const isCustom = isNew || row.row_kind === "CUSTOM";
  const [draft, setDraft] = useState(() => toDraft(row));
  const [error, setError] = useState("");
  const [extraLangs, setExtraLangs] = useState([]);
  const [focusLang, setFocusLang] = useState("");
  const namesRef = useRef(null);
  const defRef = useRef(null);

  useEffect(() => {
    const d = toDraft(row);
    setDraft(d);
    setError("");
    setExtraLangs([]);
    setFocusLang("");
  }, [row, open]);

  const std = row?.standard || { display_name: {}, aliases: {}, glossary_defs: [] };
  // 표준에서 한국어만 가진 개념(지표 이름표 등) — 다른 언어 별칭이 그 언어의 해석에 쓰인다는 안내
  const koOnly = !isCustom && Object.keys(std.display_name || {}).every((lg) => lg === "ko");

  // 별칭이 있거나 사용자가 연 언어만 줄로 그리고, 나머지는 "별칭 없음" 한 줄로 접는다
  const shownLangs = langs.filter((lg) => (std.aliases?.[lg] || []).length || draft.aliases[lg].length
    || draft.excluded_aliases[lg].length || extraLangs.includes(lg));
  const emptyLangs = langs.filter((lg) => !shownLangs.includes(lg));

  const patch = (p) => setDraft((d) => ({ ...d, ...p }));
  const patchAliases = (lg, { added, excluded }) =>
    setDraft((d) => ({ ...d, aliases: { ...d.aliases, [lg]: added }, excluded_aliases: { ...d.excluded_aliases, [lg]: excluded } }));

  const fail = (msg, ref) => {
    setError(msg);
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const save = () => {
    if (isCustom && !LOCALES.some((lg) => String(draft.display_name[lg] || "").trim())) {
      fail(t("aiTermDisplayNameRequired"), namesRef);
      return;
    }
    if (draft.note_txt.trim() && !draft.def_txt.trim()) {
      fail(t("aiTermNoteRequiresDef"), defRef);
      return;
    }
    // 표준 행을 전부 비워 저장하면 차분이 없는 것 — 되돌리기와 같다
    if (!isCustom && isEmptyDraft(draft)) {
      if (row?.id != null) onRevert(row);
      else onClose();
      return;
    }
    onSave(row, draft);
  };

  const badge = isNew ? null : STATUS_BADGE[rowStatus(row)];
  const title = (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      {isNew ? t("aiTermAddEntry") : termName(row, locale) || row.term_cd}
      {badge && (
        <Chip component="span" size="small" variant="outlined" label={t(badge.key)}
          sx={{ height: 20, fontSize: FS.CAPTION, fontWeight: 400, color: toneDark(badge.tone), borderColor: toneMain(badge.tone) }} />
      )}
    </Box>
  );


  // 아이콘은 그리드 행 동작과 같은 것을 쓴다 — 같은 동작이 목록과 모달에서 다르게 보이지 않게
  const extra = !isNew && row.id != null ? (
    <Button size="small" onClick={() => onRevert(row)}
      startIcon={isCustom ? <DeleteOutlineIcon /> : <RotateLeftIcon />}
      sx={{ color: "text.secondary", "&:hover": { color: "error.main", bgcolor: "transparent" } }}>
      {isCustom ? t("delete") : t("aiTermRevert")}
    </Button>
  ) : null;


  return (
    <BaseConfigModal open={open} onClose={onClose} title={title} onSave={save} saveDisabled={saving} width={840}
      subtitle={!isCustom ? row.term_cd : ""} extraActions={extra}>
      <Box sx={{ height: BODY_H, overflowY: "auto", pr: 0.5 }}>
        {error && <Alert severity="warning" sx={{ fontSize: FS.BODY, py: 0, mb: 1.5 }}>{error}</Alert>}

        {!isCustom && (
          <Box sx={{ bgcolor: C.bg, border: `1px solid ${C.border}`, borderRadius: 1, px: 1.5, py: 1.25, mb: 2.5 }}>
            <SectionHead label={t("displayName")} />
            <Box sx={{ display: "grid", gridTemplateColumns: "88px 1fr 88px 1fr 88px 1fr", rowGap: 0.5, columnGap: 1, fontSize: FS.BODY }}>
              {langs.map((lg) => (
                <Box key={lg} sx={{ display: "contents" }}>
                  <Box component="span" sx={{ color: "text.secondary" }}>{LOCALE_LABELS[lg]}</Box>
                  <Box component="span">{std.display_name?.[lg] || ""}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {isCustom && (
          <Box ref={namesRef} sx={{ mb: 2.5 }}>
            <SectionHead label={t("displayName")} right={<Caption>{t("aiTermDisplayNameMin")}</Caption>} />
            <Box sx={{ display: "grid", gridTemplateColumns: "88px 1fr 88px 1fr", alignItems: "center", rowGap: 1, columnGap: 1 }}>
              {langs.map((lg) => (
                <Box key={lg} sx={{ display: "contents" }}>
                  <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{LOCALE_LABELS[lg]}</Typography>
                  <TextField size="small" value={draft.display_name[lg] || ""} inputProps={{ maxLength: NAME_MAX_LEN }}
                    sx={{ "& .MuiInputBase-input": { py: 0.75, fontSize: FS.BODY } }}
                    onChange={(e) => patch({ display_name: { ...draft.display_name, [lg]: e.target.value } })} />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ mb: 2.5 }}>
          <SectionHead label={t("aiTermAliases")} right={isCustom ? null : (
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <LegendChip label={t("aiTermStatusStandard")} sx={{ bgcolor: C.bg3, color: C.text1 }} />
              <LegendChip label={t("aiTermStatusAdded")} sx={{ ...addedSx(isCustom ? "added" : "override") }} />
              <LegendChip label={t("aiTermExclude")} sx={{ color: toneMain("excluded"), boxShadow: (th) => `inset 0 0 0 1px ${toneMain("excluded")(th)}`, textDecoration: "line-through" }} />
            </Box>
          )} />
          {koOnly && <Caption sx={{ mb: 0.5 }}>{t("aiTermLabelOnlyNotice")}</Caption>}
          {shownLangs.map((lg) => (
            <TermAliasRow key={lg} locale={lg} standard={std.aliases?.[lg] || []} tone={isCustom ? "added" : "override"}
              added={draft.aliases[lg]} excluded={draft.excluded_aliases[lg]}
              autoFocus={focusLang === lg} onChange={(v) => patchAliases(lg, v)} />
          ))}
          {emptyLangs.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.5 }}>
              <Typography sx={{ width: 88, flexShrink: 0, fontSize: FS.BODY, color: "text.disabled" }}>
                {t("aiTermNoAliases")}
              </Typography>
              {/* 언어 칩을 누르면 그 언어 줄이 열리고 입력칸에 바로 포커스 */}
              <Box sx={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {emptyLangs.map((lg) => (
                  <Chip key={lg} size="small" variant="outlined" clickable label={LOCALE_LABELS[lg]}
                    icon={<AddIcon sx={{ fontSize: "14px !important" }} />}
                    onClick={() => { setExtraLangs((v) => [...v, lg]); setFocusLang(lg); }}
                    sx={{ height: 22, fontSize: FS.BODY, borderRadius: "4px", color: "text.secondary", borderStyle: "dashed" }} />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Box ref={defRef}>
          <SectionHead label={t("aiTermDefinition")} />
          <Box sx={{ display: "grid", gridTemplateColumns: isCustom ? "1fr" : "240px 1fr", gap: 1.5, alignItems: "start" }}>
            {!isCustom && (
              <Box sx={{ bgcolor: C.bg, border: `1px solid ${C.border}`, borderRadius: 1, p: 1.25 }}>
                <Caption sx={{ mb: 0.5 }}>{t("aiTermStandardDef")}</Caption>
                {(std.glossary_defs || []).length === 0 ? (
                  <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t("aiTermNoStandardDef")}</Typography>
                ) : std.glossary_defs.map((g) => (
                  <Typography key={g.headword} sx={{ fontSize: FS.BODY, color: "text.secondary", whiteSpace: "pre-wrap", mb: 0.5 }}>
                    {g.definition}
                  </Typography>
                ))}
              </Box>
            )}
            <Box>
              <TextField fullWidth multiline minRows={2} size="small" label={t("aiTermDefinition")} value={draft.def_txt}
                inputProps={{ maxLength: DEF_MAX_LEN }} helperText={`${draft.def_txt.length}/${DEF_MAX_LEN}`}
                onChange={(e) => patch({ def_txt: e.target.value })} sx={{ mb: 1, ...TEXT_FIELD_SX }} />
              <TextField fullWidth multiline minRows={3} size="small" label={t("aiTermNote")} value={draft.note_txt}
                inputProps={{ maxLength: NOTE_MAX_LEN }} helperText={`${draft.note_txt.length}/${NOTE_MAX_LEN}`}
                onChange={(e) => patch({ note_txt: e.target.value })} sx={{ mb: 0.5, ...TEXT_FIELD_SX }} />
              <Caption>{t("aiTermLanguageFreeNotice")}</Caption>
            </Box>
          </Box>
        </Box>
      </Box>
    </BaseConfigModal>
  );
};

export default TermEditDialog;
